import { observer } from "mobx-react";
import { useMemo, useState } from "react";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import { PartnerCustomerInventoryTag } from "../../../../../models/inventory/PartnerCustomerInventoryTag";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import CreateInventoryTagPopup from "./CreateInventoryTagPopup";

interface InventoryTagSelectProps {
  tags: PartnerCustomerInventoryTag[];
  tagId: string;
  tagLabel?: string;
  onChange: (tagId: string, tagLabel: string) => void;
  /** When set (and the admin may create tags), shows "+ Add" in the dropdown. */
  onCreateTag?: (
    tagId: string,
    tag: string,
  ) => Promise<PartnerCustomerInventoryTag>;
  title?: string;
  disabled?: boolean;
  fontSize?: string;
}

/**
 * Single-select partner tag picker. Search matches the tag name or the tag code;
 * when nothing fits, the admin can create a new tag and it gets selected.
 */
const InventoryTagSelect: React.FC<InventoryTagSelectProps> = observer(
  ({
    tags,
    tagId,
    tagLabel,
    onChange,
    onCreateTag,
    title = "Select tag",
    disabled = false,
    fontSize = "12px",
  }) => {
    const { userStore } = useStores();
    const [createOpen, setCreateOpen] = useState(false);
    const [createInitialTag, setCreateInitialTag] = useState("");

    const canCreateTag =
      !!onCreateTag &&
      userStore.hasPermission([
        EmployeeRoles.CREATE_SWAGPACK,
        EmployeeRoles.EDIT_SWAGPACK,
      ]);

    const tagOptions: FilterValue[] = useMemo(
      () =>
        tags
          .filter((tag) => tag.active !== 0)
          .map((tag) => ({
            id: String(tag.tag_id),
            value: tag.tag,
            subtitle: tag.tag_id,
          })),
      [tags],
    );

    const selectedLabel =
      tagLabel ||
      tags.find((tag) => String(tag.tag_id) === tagId)?.tag ||
      tagId;

    return (
      <>
        <AppSearchDropdownMulti
          isMultiSelect={false}
          title={title}
          local
          list={tagOptions}
          value={tagId ? [{ id: tagId, value: selectedLabel }] : []}
          handleSelect={(selected) => {
            if (!selected.length) {
              onChange("", "");
              return;
            }
            onChange(String(selected[0].id), selected[0].value);
          }}
          disabled={disabled}
          fontSize={fontSize}
          minWidth="100%"
          onCreateNew={
            canCreateTag
              ? (searchText) => {
                  setCreateInitialTag(searchText);
                  setCreateOpen(true);
                }
              : undefined
          }
          createNewLabel={(searchText) =>
            searchText ? `+ Add new tag "${searchText}"` : "+ Add new tag"
          }
        />
        {canCreateTag && onCreateTag && (
          <CreateInventoryTagPopup
            open={createOpen}
            initialTag={createInitialTag}
            onClose={() => setCreateOpen(false)}
            onCreate={onCreateTag}
            onCreated={(created) => {
              setCreateOpen(false);
              onChange(String(created.tag_id), created.tag);
            }}
          />
        )}
      </>
    );
  },
);

export default InventoryTagSelect;
